// Client-Filter für den Signieren-Tab: Titel-/Bezugs-Suche und Rubrik-Chips der
// Vorlagen. Rein und vitest-getestet — die Liste filtert damit clientseitig.

import { trifftText } from '../suche/textSuche';

export const OHNE_RUBRIK = 'Ohne Rubrik';

/** Titel- und Bezugs-Suche über die gemeinsame Regel in lib/suche/textSuche:
 *  normalisiert (Satzzeichen/Umlaute), mehrere Wörter müssen alle treffen. */
export function passtSuche(
  v: { titel: string; bezug?: Record<string, string | null> | null },
  begriff: string,
): boolean {
  const bezug = v.bezug || {};
  const felder = [v.titel, ...['objekt_name', 'einheit_name', 'mieter_name', 'eigentuemer_name'].map((k) => bezug[k])];
  return trifftText(felder, begriff);
}

export function rubrikenAus(vorlagen: { vorlage_kategorie?: string | null }[]): string[] {
  const set = new Set<string>();
  let ohne = false;
  for (const v of vorlagen) {
    const r = (v.vorlage_kategorie || '').trim();
    if (r) set.add(r); else ohne = true;
  }
  const sortiert = [...set].sort((a, b) => a.localeCompare(b, 'de'));
  return ohne ? [...sortiert, OHNE_RUBRIK] : sortiert;
}

export function passtRubrik(vorlage: { vorlage_kategorie?: string | null }, chip: string | null): boolean {
  if (chip === null) return true;
  const r = (vorlage.vorlage_kategorie || '').trim();
  if (chip === OHNE_RUBRIK) return r === '';
  return r === chip;
}

// ── Objekt, Status und Zeitraum ──────────────────────────────────────────────
// Der Signieren-Bereich wächst mit jedem Mietvertrag und jeder Vereinbarung. Titel-Suche
// allein reicht dafür nicht — gefragt ist „alles zu Objekt X aus 2026" (User 2026-08-12).

export const OHNE_OBJEKT = 'Ohne Objekt';

export interface FilterbarerVorgang {
  titel: string;
  status: string;
  bezug?: Record<string, string | null> | null;
  created_at: string;
}

/** Objekte, die in den Vorgängen wirklich vorkommen — alphabetisch, „Ohne Objekt" zuletzt. */
export function objekteAus(vorgaenge: FilterbarerVorgang[]): string[] {
  const set = new Set<string>();
  let ohne = false;
  for (const v of vorgaenge) {
    const name = (v.bezug?.objekt_name || '').trim();
    if (name) set.add(name); else ohne = true;
  }
  const sortiert = [...set].sort((a, b) => a.localeCompare(b, 'de'));
  return ohne ? [...sortiert, OHNE_OBJEKT] : sortiert;
}

export function passtObjekt(v: FilterbarerVorgang, wahl: string | null): boolean {
  if (!wahl) return true;
  const name = (v.bezug?.objekt_name || '').trim();
  return wahl === OHNE_OBJEKT ? name === '' : name === wahl;
}

export function passtStatus(v: FilterbarerVorgang, wahl: string | null): boolean {
  return !wahl || v.status === wahl;
}

/** Tagesgenauer Zeitraum über das Anlagedatum; leere Grenzen zählen nicht. */
export function passtZeitraum(v: FilterbarerVorgang, von: string, bis: string): boolean {
  if (!von && !bis) return true;
  const d = new Date(v.created_at);
  if (Number.isNaN(d.getTime())) return false;
  const tag = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  if (von && tag < von) return false;
  if (bis && tag > bis) return false;
  return true;
}

export interface SignaturFilter {
  suche?: string;
  objekt?: string | null;
  status?: string | null;
  von?: string;
  bis?: string;
}

/** Alle Filter zusammen — jeder Teil wirkt nur, wenn er gesetzt ist. */
export function filtereVorgaenge<T extends FilterbarerVorgang>(liste: T[], f: SignaturFilter): T[] {
  return liste.filter((v) =>
    passtSuche(v as any, f.suche || '')
    && passtObjekt(v, f.objekt ?? null)
    && passtStatus(v, f.status ?? null)
    && passtZeitraum(v, f.von || '', f.bis || ''),
  );
}

/** Ist überhaupt ein Filter gesetzt? Steuert den Hinweis „(gefiltert)". */
export function filterAktiv(f: SignaturFilter): boolean {
  return !!(f.suche?.trim() || f.objekt || f.status || f.von || f.bis);
}
